import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Mail, Phone, User, KeyRound, Calendar, Building2, Save, Loader2 } from 'lucide-react'
import { formateursAdminApi } from '../api/formateurs-admin.api'
import { useStructuresActives } from '@/features/structures/hooks/use-structures'

function getInitials(prenom: string, nom: string) {
  return `${prenom?.[0] ?? ''}${nom?.[0] ?? ''}`.toUpperCase()
}

export function FormateurDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { data: formateur, isLoading } = useQuery({
    queryKey: ['admin', 'formateurs', id],
    queryFn: () => formateursAdminApi.getById(parseInt(id!)),
    enabled: !!id,
  })

  const { data: structures } = useStructuresActives()

  const [prenom, setPrenom] = useState('')
  const [nom, setNom] = useState('')
  const [telephone, setTelephone] = useState('')
  const [selectedStructureIds, setSelectedStructureIds] = useState<number[]>([])

  useEffect(() => {
    if (formateur) {
      setPrenom(formateur.prenom)
      setNom(formateur.nom)
      setTelephone(formateur.telephone ?? '')
      setSelectedStructureIds(formateur.structureIds ?? [])
    }
  }, [formateur])

  const updateMutation = useMutation({
    mutationFn: () =>
      formateursAdminApi.update(formateur!.id!, {
        ...formateur!,
        prenom,
        nom,
        telephone,
        structureIds: selectedStructureIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'formateurs'] })
    },
  })

  const resetPassword = useMutation({
    mutationFn: () => formateursAdminApi.resetPassword(formateur!.id!),
  })

  const toggleStructure = (structureId: number) => {
    setSelectedStructureIds((prev) =>
      prev.includes(structureId) ? prev.filter((s) => s !== structureId) : [...prev, structureId],
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!formateur) {
    return (
      <div className="text-center py-12 text-neutral-500">
        {t('admin.formateurs.notFound', 'Formateur introuvable')}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/formateurs')}
          className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('admin.formateurs.title', 'Formateurs')}
        </button>
      </div>

      {/* Profile card */}
      <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-5">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/60 text-xl font-bold text-white">
            {getInitials(prenom, nom)}
          </div>
          <div className="flex-1 space-y-1">
            <h1 className="text-xl font-bold text-neutral-900">
              {prenom} {nom}
            </h1>
            <p className="text-sm text-neutral-500">
              {t('admin.formateurs.role', 'Formateur')}
            </p>
          </div>
          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
            {t('admin.formateurs.active', 'Actif')}
          </span>
        </div>
      </div>

      {/* Edit form */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Informations */}
        <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm space-y-4">
          <h2 className="text-sm font-semibold text-neutral-900">
            {t('admin.formateurs.detailContact', 'Coordonnées')}
          </h2>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                <Mail className="h-4 w-4 text-blue-500" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-neutral-400">{t('common.email', 'Email')}</p>
                <p className="text-sm font-medium text-neutral-900">{formateur.email}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-neutral-400">{t('common.firstName', 'Prénom')}</label>
                <input
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  className="h-9 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-neutral-400">{t('common.lastName', 'Nom')}</label>
                <input
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  className="h-9 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs text-neutral-400">{t('common.phone', 'Téléphone')}</label>
              <input
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                placeholder="06 12 34 56 78"
                className="h-9 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
              />
            </div>

            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-50">
                <User className="h-4 w-4 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-neutral-400">{t('admin.formateurs.detailLogin', 'Login')}</p>
                <p className="text-sm font-medium text-neutral-900">{formateur.login}</p>
              </div>
            </div>
          </div>

          {/* Reset password */}
          <div className="border-t border-neutral-100 pt-3">
            {resetPassword.isSuccess ? (
              <p className="text-xs text-emerald-600">Mot de passe réinitialisé et envoyé par email</p>
            ) : (
              <button
                onClick={() => resetPassword.mutate()}
                disabled={resetPassword.isPending}
                className="flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50"
              >
                {resetPassword.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
                Réinitialiser le mot de passe
              </button>
            )}
          </div>
        </div>

        {/* Structures + Affectations */}
        <div className="space-y-6">
          {/* Structures */}
          <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-neutral-400" />
              <h2 className="text-sm font-semibold text-neutral-900">Structures</h2>
            </div>

            {structures && structures.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {structures.map((s) => {
                  const isSelected = selectedStructureIds.includes(s.id)
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleStructure(s.id)}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-all ${
                        isSelected
                          ? 'border-primary/40 bg-primary/10 text-primary shadow-sm'
                          : 'border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300'
                      }`}
                    >
                      <span className={`h-2 w-2 rounded-full ${isSelected ? 'bg-primary' : 'bg-neutral-300'}`} />
                      {s.nom}
                      {s.ville && <span className="text-neutral-400">({s.ville})</span>}
                    </button>
                  )
                })}
              </div>
            ) : (
              <p className="text-sm text-neutral-400">Aucune structure disponible</p>
            )}
          </div>

          {/* Affectations */}
          <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-neutral-400" />
              <h2 className="text-sm font-semibold text-neutral-900">
                {t('admin.formateurs.detailAffectations', 'Affectations')}
              </h2>
            </div>

            {formateur.affectations && formateur.affectations.length > 0 ? (
              <div className="space-y-2">
                {formateur.affectations.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-lg border border-neutral-100 bg-neutral-50 px-3 py-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-50">
                      <Calendar className="h-4 w-4 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-neutral-900">{a.societe}</p>
                      <p className="text-xs text-neutral-400">{a.dateAffectation}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-400">
                {t('admin.formateurs.noAffectations', 'Aucune affectation')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end gap-3">
        {updateMutation.isSuccess && (
          <p className="flex items-center text-sm text-emerald-600">Modifications enregistrées</p>
        )}
        {updateMutation.isError && (
          <p className="flex items-center text-sm text-red-600">Erreur lors de la sauvegarde</p>
        )}
        <button
          onClick={() => updateMutation.mutate()}
          disabled={updateMutation.isPending}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
        >
          {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Enregistrer
        </button>
      </div>
    </div>
  )
}
